import { useEffect, useState } from "react";
import { useSelector } from "react-redux";
import { NavLink, useNavigate, useParams } from "react-router";

import * as Api from "@/api";
import { NEW_PARTNER } from "@/constants/New";
import Avatar from "@/design/components/Avatar";
import DeleteFlow from "@/design/components/DeleteFlow";
import HeaderCard from "@/design/components/HeaderCard";
import SaveIndicator from "@/design/components/SaveIndicator";
import Search from "@/design/components/Search";
import FormGroup from "@/design/forms/FormGroup";
import InputAutocomplete from "@/design/forms/InputAutocomplete";
import InputText from "@/design/forms/InputText";
import { string } from "@/utils/hooks/endorse";
import useSave, { SaveState } from "@/utils/hooks/useSave";
import useSearch from "@/utils/hooks/useSearch";
import { toggleLoadingState } from "@/utils/utility";
import Valkyrie, { viArrowLeft, viAt, viGear, viStore, viTag } from "@sippy-platform/valkyrie";

import {
  Box,
  Chip,
  Container,
  Grid,
  IconButton,
  List,
  ListItem,
  ListItemAvatar,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Stack,
  Typography,
  useMediaQuery,
  useTheme,
} from "@mui/material";

import partnerReducer from "./partnerReducer";
import PbxCard from "./PbxCard";

function Partner() {
  const { id } = useParams();
  const theme = useTheme();
  const matches = useMediaQuery(theme.breakpoints.up("md"));
  const navigate = useNavigate();

  const auth = useSelector((state) => state.auth);

  const [searchValue, setSearchValue] = useState("");
  const [tab, setTab] = useState(() => (id === "new" ? 1 : 0));
  const [previousId, setPreviousId] = useState(id);

  if (id !== previousId) {
    setPreviousId(id);
    setTab(id === "new" ? 1 : 0);
  }

  const handleTab = (event, newTab) => setTab(newTab);

  const {
    value: partner,
    dispatch: partnerDispatch,
    setSaveState,
    formState,
    errors,
    allErrors,
    save,
  } = useSave({
    initialValue: NEW_PARTNER,
    reducer: partnerReducer,
    validator: () => ({
      name: string()
        .required({ message: "Name is required." })
        .length([1, 80], { message: "Name must be at most 80 characters." }),
      supportEmail: string().email({
        message: "Support email address isn't a valid format.",
      }),
      salesEmail: string().email({
        message: "Sales email address isn't a valid format.",
      }),
    }),
    onSubmit: () => Api.storePartner(partner),
    onSuccess: (response) => {
      if (!partner.id) {
        partnerDispatch({ type: "id", value: response.data.partner.id });
        navigate(`../${response.data.partner.id}`);
      }

      Promise.resolve(Api.getPartners());
    },
  });

  // Get general data
  useEffect(() => {
    let ignore = false;

    if (Number(id) === partner.id || !id) {
      return;
    }

    if (id === "new") {
      Api.cancelActiveGetRequests();
      partnerDispatch({ type: "set", value: NEW_PARTNER });

      return () => {
        ignore = true;
      };
    }

    toggleLoadingState(true);

    Promise.resolve(Api.getPartner({ id: Number(id) }))
      .then((response) => {
        if (!ignore) {
          setTab(0);
          partnerDispatch({ type: "set", value: response.data.partner });
        }
      })
      .finally(() => toggleLoadingState(false));

    return () => {
      ignore = true;
    };
  }, [id, partner.id, partnerDispatch]);

  const searchResults = useSearch(partner.pbxes || [], searchValue, ["name", "context"]);

  // Handle changes
  const formHandler = (property, value) => {
    partnerDispatch({ type: property, value });
    setSaveState(SaveState.UNSAVED);
  };

  // Delete
  function onDeleteFinish() {
    Promise.resolve(Api.getPartners()).then(() => navigate(".."));
  }

  return (
    <Container>
      <title>{`${partner.name || "Partner"} \u00b7 ${import.meta.env.SIPPY_APP_TITLE}`}</title>
      <Stack
        sx={{
          gap: 2,
        }}
      >
        <HeaderCard
          tab={tab}
          changeTab={handleTab}
          tabs={[
            {
              label: "Overview",
              icon: viStore,
              id: "overview",
              disabled: !partner.id,
            },
            { label: "Settings", icon: viGear, id: "settings" },
          ]}
          avatar={
            !matches ? (
              <IconButton variant="avatar" component={NavLink} to={".."}>
                <Valkyrie icon={viArrowLeft} />
                <Avatar text={partner.name} icon={viStore} />
              </IconButton>
            ) : (
              <Avatar text={partner.name} icon={viStore} />
            )
          }
          action={
            <>
              <SaveIndicator formState={formState} errors={allErrors} save={save} />
              {auth.accessLevel === 0 && (
                <DeleteFlow
                  key={partner.id || "nk"}
                  isActionable={!!partner?.id}
                  onDelete={() => Api.deletePartner(partner)}
                  name={partner.name}
                  deleteInfo="Are you sure you want to delete this partner? All linked PBXes will be removed as well."
                  onDeleteFinish={onDeleteFinish}
                />
              )}
            </>
          }
          title={partner.name || "Unnamed partner"}
          subheader={`${partner.pbxes?.length ?? 0} ${partner.pbxes?.length === 1 ? "PBX" : "PBXes"}`}
        />
        {tab === 0 && (
          <Grid container spacing={2}>
            <Grid size={{ xs: 12, md: 5, lg: 4, xl: 3 }}>
              <Stack
                sx={{
                  gap: 2,
                }}
              >
                <FormGroup title="Details">
                  <Grid size={12}>
                    <List sx={{ m: -2 }}>
                      {partner.defaultPbx && (
                        <ListItem
                          disablePadding
                          secondaryAction={
                            <IconButton component={NavLink} to={`/pbx/${partner.defaultPbx.context}/my-pbx`}>
                              <Valkyrie icon={viGear} />
                            </IconButton>
                          }
                        >
                          <ListItemButton
                            component={NavLink}
                            to={`/pbx/${partner.defaultPbx.context}/dashboard`}
                            sx={{ borderRadius: 0 }}
                          >
                            <ListItemAvatar>
                              <Avatar text={partner.defaultPbx.name} />
                            </ListItemAvatar>
                            <ListItemText primary={partner.defaultPbx?.name} secondary="Default PBX" />
                          </ListItemButton>
                        </ListItem>
                      )}
                      {partner.supportEmail && (
                        <ListItem>
                          <ListItemIcon style={{ marginLeft: 4, width: 40 }}>
                            <Valkyrie icon={viAt} />
                          </ListItemIcon>
                          <ListItemText primary={partner.supportEmail} secondary="Support" />
                        </ListItem>
                      )}
                      {partner.salesEmail && (
                        <ListItem>
                          <ListItemIcon style={{ marginLeft: 4, width: 40 }}>
                            <Valkyrie icon={viTag} />
                          </ListItemIcon>
                          <ListItemText primary={partner.salesEmail} secondary="Sales" />
                        </ListItem>
                      )}
                    </List>
                  </Grid>
                </FormGroup>
                <FormGroup title="Accounts">
                  <Grid size={12}>
                    {partner.accounts?.length > 0 ? (
                      partner.accounts?.map((user) => (
                        <Chip
                          key={user.id}
                          avatar={<Avatar text={`${user.firstname} ${user.lastname ?? ""}`} />}
                          label={`${user.firstname} ${user.lastname ?? ""}`}
                          onClick={() => navigate(`/backstage/accounts/${user.id}`)}
                        />
                      ))
                    ) : (
                      <Typography>No accounts</Typography>
                    )}
                  </Grid>
                </FormGroup>
              </Stack>
            </Grid>
            <Grid size={{ xs: 12, md: 7, lg: 8, xl: 9 }}>
              <FormGroup title="PBXes">
                <Grid size={12}>
                  <Search id="search-content" accessKey="q" value={searchValue} onChange={setSearchValue} />
                </Grid>
                <Grid size={12}>
                  {searchResults.length > 0 ? (
                    <Box
                      sx={{
                        display: "grid",
                        gridTemplateColumns: "repeat(auto-fill, minmax(min(14rem, 100%), 1fr))",
                        gap: 1,
                      }}
                    >
                      {searchResults.map((pbx) => (
                        <PbxCard pbx={pbx} key={pbx.id} />
                      ))}
                    </Box>
                  ) : (
                    <Typography>This partner has no linked accounts.</Typography>
                  )}
                </Grid>
              </FormGroup>
            </Grid>
          </Grid>
        )}
        {tab === 1 && (
          <>
            <FormGroup title="Identity">
              <Grid size={{ xs: 12, sm: 6, md: 12, lg: 6 }}>
                <InputText required id="name" label="Name" onChange={formHandler} value={partner.name} error={errors} />
              </Grid>
              <Grid size={{ xs: 12, sm: 6, md: 12, lg: 6 }}>
                <InputText
                  id="partnerReference"
                  label="Partner reference"
                  onChange={formHandler}
                  value={partner.partnerReference}
                  error={errors}
                />
              </Grid>
              {partner.id && partner.pbxes?.length > 0 && (
                <Grid size={{ xs: 12, sm: 6, md: 12, lg: 6 }}>
                  <InputAutocomplete
                    id="defaultPbx"
                    label="Default PBX"
                    onChange={formHandler}
                    value={partner.defaultPbx?.id}
                    error={errors}
                    selectValue="id"
                    selects={partner.pbxes}
                    getOptionLabel={(option) => option.name}
                    hasEmpty={null}
                  />
                </Grid>
              )}
              <Grid size={{ xs: 12, sm: 6, md: 12, lg: 6 }}>
                <InputText
                  id="supportEmail"
                  label="Support email"
                  onChange={formHandler}
                  value={partner.supportEmail}
                  error={errors}
                />
              </Grid>
              <Grid size={{ xs: 12, sm: 6, md: 12, lg: 6 }}>
                <InputText
                  id="salesEmail"
                  label="Sales email"
                  onChange={formHandler}
                  value={partner.salesEmail}
                  error={errors}
                />
              </Grid>
            </FormGroup>
          </>
        )}
      </Stack>
    </Container>
  );
}

export default Partner;
